import type { KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import type { ICommitAssignee } from '@apis/activities';
import styles from './AssigneeChips.module.css';

// ---------------------------------------------------------------------------
// AssigneeChips — who is accountable for the promise: one chip per project
// user plus "Unassigned", exactly one checked. Asked by both commit reviews
// (a leaf's, and the list form's shared answer for every leaf).
//
// A real radio group for the keyboard, not just for the screen reader: the
// group is ONE tab stop (roving tabindex — the checked chip, or the first when
// the answer is none of them), and the arrow keys move the choice along it,
// wrapping at the ends; Home / End jump to the first / last.
// ---------------------------------------------------------------------------

interface IAssigneeChipsProps {
  candidates: readonly ICommitAssignee[];
  /** The checked username; `null` is Unassigned. */
  value: string | null;
  onPick: (username: string | null) => void;
}

/** Where each navigation key moves the choice, from `index` among `count`. */
const MOVES: Partial<Record<string, (index: number, count: number) => number>> = {
  ArrowRight: (index, count) => (index + 1) % count,
  ArrowDown: (index, count) => (index + 1) % count,
  ArrowLeft: (index, count) => (index - 1 + count) % count,
  ArrowUp: (index, count) => (index - 1 + count) % count,
  Home: () => 0,
  End: (_index, count) => count - 1,
};

export function AssigneeChips({ candidates, value, onPick }: IAssigneeChipsProps) {
  const { t } = useTranslation();
  const chips = [
    ...candidates.map((candidate) => {
      const name = candidate.name ?? candidate.username;
      return { username: candidate.username, label: candidate.isYou ? t('scheduler.commit.assigneeYou', { name }) : name };
    }),
    { username: null, label: t('scheduler.commit.unassigned') },
  ];
  const tabStop = Math.max(chips.findIndex((chip) => chip.username === value), 0);

  // Keys land on the group; the focused chip is the one they started from (the
  // chips are its only focusable content). Moving focuses the next chip and
  // clicks it, so the choice follows the focus the way a radio group's does.
  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    const move = MOVES[e.key];
    if (!move) return;
    e.preventDefault();
    const radios = e.currentTarget.querySelectorAll<HTMLButtonElement>('[role="radio"]');
    const next = radios.item(move([...radios].indexOf(e.target as HTMLButtonElement), radios.length));
    next.focus();
    next.click();
  };

  return (
    <div className={styles.chips} role="radiogroup" aria-label={t('scheduler.commit.row.assignee')} onKeyDown={onKeyDown}>
      {chips.map((chip, index) => (
        <button
          key={chip.username ?? ''}
          type="button"
          role="radio"
          className={styles.chip}
          aria-checked={value === chip.username}
          tabIndex={index === tabStop ? 0 : -1}
          onClick={() => onPick(chip.username)}
        >
          {chip.label}
        </button>
      ))}
    </div>
  );
}
